'use client'

import { cx } from 'lib/cx'
import NextImage from 'next/image'
import { useState } from 'react'
import { twMerge } from 'tailwind-merge'

function Initials({
  person,
  className,
  isSquare,
}: {
  person?: Omit<Person, 'image'>
  className?: string
  isSquare?: boolean
}) {
  const splitName = person?.name?.split(' ')
  const firstLetter =
    person?.firstName?.[0] ??
    splitName?.[0]?.[0] ??
    person?.email?.[0]?.toUpperCase() ??
    ''
  const secondLetter = person?.lastName?.[0] ?? splitName?.[1]?.[0] ?? ''
  const displayName =
    person?.name ??
    [person?.firstName, person?.lastName].filter(Boolean).join(' ')

  return (
    (firstLetter || secondLetter) && (
      <span
        title={displayName || person?.email || ''}
        className={twMerge(
          'bg-text-placeholder relative inline-flex h-10 w-10 shrink-0 items-center justify-center text-xs leading-none font-normal text-white select-none',
          isSquare ? 'rounded-none' : 'rounded-full',
          className,
        )}
      >
        {firstLetter}
        {secondLetter}
      </span>
    )
  )
}

export type Person = {
  firstName?: string | null
  lastName?: string | null
  name?: string | null
  image?: string | null
  email?: string | null
  alias?: string | null
}

export type AvatarProps = {
  employee?: Person
  className?: string
  isSquare?: true
  sizes?: string
}
export default function Avatar({
  employee,
  className,
  isSquare,
  sizes,
}: AvatarProps) {
  const [hasError, setHasError] = useState(false)
  const showImage = employee?.image && !hasError

  return showImage ? (
    <span
      className={twMerge(
        'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full text-center text-xs',
        isSquare ? 'rounded-none' : 'rounded-full',
        className,
      )}
    >
      <NextImage
        src={employee?.image || ''}
        className={cx(
          'bg-text-placeholder !m-0',
          isSquare ? 'rounded-none' : 'rounded-full',
        )}
        alt={`Avatar of ${employee?.name}`}
        title={employee?.name || undefined}
        fill
        sizes={sizes ?? (isSquare ? undefined : '4rem')}
        loading="lazy"
        onError={() => {
          setHasError(true)
        }}
      />
    </span>
  ) : (
    <Initials person={employee} className={className} isSquare={isSquare} />
  )
}

export function AvatarSkeleton({ className }: { className?: string }) {
  return (
    <span
      className={twMerge(
        'bg-text-placeholder relative inline-flex h-10 w-10 animate-pulse items-center justify-center rounded-full text-xs leading-none font-normal text-white select-none',
        className,
      )}
    />
  )
}
